/**
 * The one table.
 *
 * Twelve list pages each hand-rolled a filter card, a `<table>`, a loading
 * spinner, an empty sentence and a Previous/Next pair. They drifted: some had
 * hover states, some didn't; none could sort; the empty state was a different
 * shade of grey on every page. This component is that shape, once, with the
 * things a console needs and none of them had — server-driven sort, row
 * selection with bulk actions, column visibility, density, skeletons that match
 * the real row height, and an error state you can retry from.
 *
 * Sorting/paging are deliberately *server* concerns here: the API returns one
 * page, so sorting the 20 rows in the browser would silently sort a slice and
 * call it the answer. Sort state goes to the URL, the URL goes to the API.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type VisibilityState,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Inbox, RotateCw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableScroll,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { PaginationBar } from './pagination-bar';
import { TableToolbar } from './table-toolbar';
import { BulkBar } from './bulk-bar';
import { use_table_prefs } from './use-table-prefs';
import { dated_filename, download_csv, to_csv } from './csv';
import { error_message } from '@/components/ui/toaster';
import type { SortState } from '@/hooks/use-url-state';
import { cn } from '@/lib/utils';
import './types';

export type DataTableProps<T> = {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  total: number;
  get_row_id: (row: T) => string;

  page: number;
  page_size: number;
  on_page_change: (next: number) => void;
  on_page_size_change?: (next: number) => void;

  sort?: SortState;
  on_sort_change?: (next: SortState) => void;

  is_loading?: boolean;
  is_fetching?: boolean;
  error?: unknown;
  on_retry?: () => void;

  /** Clicking anywhere in the row opens this. */
  row_href?: (row: T) => string;
  /** Trailing per-row menu; receives the row, returns menu items. */
  row_actions?: (row: T) => React.ReactNode;

  selectable?: boolean;
  bulk_actions?: (ctx: { ids: string[]; rows: T[]; clear: () => void }) => React.ReactNode;

  /** The filter bar. Rendered above the toolbar, inside the same panel. */
  filters?: React.ReactNode;
  /** Page-specific buttons in the toolbar (e.g. "New banner"). */
  toolbar_extra?: React.ReactNode;

  /** localStorage namespace for density + column visibility. */
  storage_key: string;
  /** Enables CSV export; used as the filename stem. */
  export_name?: string;
  /** Server-side "all matching rows" export. Omit to offer page-only export. */
  on_export_all?: () => void | Promise<void>;

  empty?: { icon?: LucideIcon; title?: string; description?: string; action?: React.ReactNode };
  /** Screen-reader description of what the table lists. */
  caption?: string;
  test_id?: string;
};

/** Don't hijack a click that was meant for something inside the row. */
function is_interactive_target(el: EventTarget | null): boolean {
  const selector = 'a,button,input,select,textarea,label,[role="menu"],[data-no-row-nav]';
  return el instanceof Element && !!el.closest(selector);
}

export function DataTable<T>({
  columns,
  data,
  total,
  get_row_id,
  page,
  page_size,
  on_page_change,
  on_page_size_change,
  sort = null,
  on_sort_change,
  is_loading = false,
  is_fetching = false,
  error,
  on_retry,
  row_href,
  row_actions,
  selectable = false,
  bulk_actions,
  filters,
  toolbar_extra,
  storage_key,
  export_name,
  on_export_all,
  empty,
  caption,
  test_id,
}: DataTableProps<T>) {
  const navigate = useNavigate();
  const [selection, set_selection] = useState<RowSelectionState>({});
  const [exporting, set_exporting] = useState(false);

  const default_visibility = useMemo(() => {
    const v: VisibilityState = {};
    for (const c of columns) {
      const id = (c.id ?? (c as { accessorKey?: string }).accessorKey) as string | undefined;
      if (id && c.meta?.hidden_by_default) v[id] = false;
    }
    return v;
  }, [columns]);

  const { prefs, set_density, set_columns, reset_columns } = use_table_prefs(storage_key, {
    columns: default_visibility,
  });

  const all_columns = useMemo<ColumnDef<T, unknown>[]>(() => {
    const cols: ColumnDef<T, unknown>[] = [];
    if (selectable) {
      cols.push({
        id: '__select',
        enableHiding: false,
        meta: { width: '1%', label: 'Select' },
        header: ({ table }) => (
          <input
            type="checkbox"
            className="console-checkbox"
            // Indeterminate is the honest state when some-but-not-all of the
            // page is picked; a plain unchecked box implies clicking it selects
            // nothing new.
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomePageRowsSelected();
            }}
            checked={table.getIsAllPageRowsSelected()}
            onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
            aria-label="Select all rows on this page"
            data-testid="select-all"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="console-checkbox"
            checked={row.getIsSelected()}
            onChange={(e) => row.toggleSelected(e.target.checked)}
            aria-label="Select row"
            data-testid={`select-${row.id}`}
          />
        ),
      });
    }
    cols.push(...columns);
    if (row_actions) {
      cols.push({
        id: '__actions',
        enableHiding: false,
        meta: { width: '1%', align: 'right', label: 'Actions' },
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => <div data-no-row-nav>{row_actions(row.original)}</div>,
      });
    }
    return cols;
  }, [columns, selectable, row_actions]);

  const table = useReactTable({
    data,
    columns: all_columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => get_row_id(row),
    state: { rowSelection: selection, columnVisibility: prefs.columns },
    onRowSelectionChange: set_selection,
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(prefs.columns) : updater;
      set_columns(next as Record<string, boolean>);
    },
    enableRowSelection: selectable,
    // Sorting and paging happen on the server; telling TanStack they're manual
    // stops it silently re-sorting the current page on top of the API's order.
    manualSorting: true,
    manualPagination: true,
  });

  const selected_ids = Object.keys(selection).filter((k) => selection[k]);
  const selected_rows = data.filter((r) => selection[get_row_id(r)]);
  const clear_selection = useCallback(() => set_selection({}), []);

  const toggle_sort = useCallback(
    (key: string) => {
      if (!on_sort_change) return;
      // First click on a new column sorts descending: for every column an
      // admin actually sorts by — created_at, amount, age of a queue item —
      // the interesting end is the top one.
      if (sort?.by !== key) return on_sort_change({ by: key, dir: 'desc' });
      if (sort.dir === 'desc') return on_sort_change({ by: key, dir: 'asc' });
      on_sort_change(null);
    },
    [on_sort_change, sort],
  );

  const visible_leaf = table.getVisibleLeafColumns();

  const export_page = useCallback(() => {
    if (!export_name) return;
    const cols = visible_leaf.filter((c) => !c.id.startsWith('__'));
    const headers = cols.map((c) => c.columnDef.meta?.label ?? c.id);
    const rows = table.getRowModel().rows.map((r) =>
      cols.map((c) => {
        const project = c.columnDef.meta?.csv;
        if (project) return project(r.original) ?? '';
        const value = r.getValue(c.id);
        // Only primitives survive a CSV cell; a rendered badge would land as
        // "[object Object]".
        return value === null || value === undefined || typeof value === 'object' ? '' : value;
      }),
    );
    download_csv(dated_filename(export_name), to_csv(headers, rows as unknown[][]));
  }, [export_name, table, visible_leaf]);

  const export_all = useCallback(async () => {
    if (!on_export_all) return;
    set_exporting(true);
    try {
      await on_export_all();
    } finally {
      set_exporting(false);
    }
  }, [on_export_all]);

  const col_count = visible_leaf.length;
  const EmptyIcon = empty?.icon ?? Inbox;

  return (
    <div className="console-panel overflow-hidden" data-testid={test_id}>
      {filters}

      <TableToolbar
        table={table}
        total={total}
        selected_count={selected_ids.length}
        density={prefs.density}
        on_density_change={set_density}
        on_reset_columns={reset_columns}
        is_fetching={is_fetching && !is_loading}
        on_refresh={on_retry}
        on_export_page={export_name ? export_page : undefined}
        on_export_all={on_export_all ? export_all : undefined}
        is_exporting={exporting}
        extra={toolbar_extra}
      />

      <div data-density={prefs.density}>
        <TableScroll>
          <Table>
            {caption && <caption className="sr-only">{caption}</caption>}
            <TableHeader>
              <TableRow className="!bg-transparent hover:!bg-transparent">
                {table.getHeaderGroups()[0]?.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  const sort_key = meta?.sort_key;
                  return (
                    <TableHead
                      key={header.id}
                      align={meta?.align}
                      style={meta?.width ? { width: meta.width } : undefined}
                      className={meta?.cell_class}
                      sortable={!!sort_key && !!on_sort_change}
                      sort_dir={sort_key && sort?.by === sort_key ? sort.dir : false}
                      on_sort={sort_key ? () => toggle_sort(sort_key) : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>

            <TableBody>
              {is_loading ? (
                <SkeletonRows columns={col_count} rows={Math.min(page_size, 10)} />
              ) : error ? (
                <tr>
                  <td colSpan={col_count} className="p-0">
                    <StateBlock
                      icon={AlertTriangle}
                      tone="destructive"
                      title="Couldn't load this list"
                      description={error_message(error, 'The request failed.')}
                      action={
                        on_retry && (
                          <Button size="sm" variant="outline" onClick={on_retry}>
                            <RotateCw className="h-4 w-4" /> Try again
                          </Button>
                        )
                      }
                      test_id="table-error"
                    />
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={col_count} className="p-0">
                    <StateBlock
                      icon={EmptyIcon}
                      title={empty?.title ?? 'Nothing here yet'}
                      description={empty?.description ?? 'No records match the current filters.'}
                      action={empty?.action}
                      test_id="table-empty"
                    />
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const href = row_href?.(row.original);
                  return (
                    <TableRow
                      key={row.id}
                      selected={row.getIsSelected()}
                      clickable={!!href}
                      data-testid={`row-${row.id}`}
                      onClick={
                        href
                          ? (e) => {
                              if (is_interactive_target(e.target)) return;
                              // A click that ended a text selection was meant
                              // to copy an id, not to navigate away from it.
                              if (window.getSelection()?.toString()) return;
                              navigate(href);
                            }
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta;
                        return (
                          <TableCell key={cell.id} align={meta?.align} className={meta?.cell_class}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </div>

      {bulk_actions && (
        <BulkBar count={selected_ids.length} on_clear={clear_selection}>
          {bulk_actions({ ids: selected_ids, rows: selected_rows, clear: clear_selection })}
        </BulkBar>
      )}

      {!error && (
        <PaginationBar
          page={page}
          page_size={page_size}
          total={total}
          is_fetching={is_fetching}
          on_page_change={on_page_change}
          on_page_size_change={on_page_size_change}
        />
      )}
    </div>
  );
}

/**
 * Skeleton rows, not a centred spinner.
 *
 * They occupy the same height the real rows will, so the header, toolbar and
 * pagination don't jump when data lands — and they tell you the shape of what's
 * coming, which a spinner in a 300px-tall empty card does not.
 */
function SkeletonRows({ columns, rows }: { columns: number; rows: number }) {
  const widths = [70, 55, 40, 60, 45, 35];
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="[&:nth-child(even)]:bg-muted/25">
          {Array.from({ length: columns }).map((__, c) => (
            <td key={c} className="row-pad border-b border-border/60">
              <div
                className="relative h-3.5 overflow-hidden rounded-pill bg-muted"
                // Staggered widths so it reads as text, not as a progress bar.
                style={{ width: `${widths[(r + c) % widths.length]}%` }}
              >
                <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-foreground/[0.06] to-transparent" />
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function StateBlock({
  icon: Icon,
  title,
  description,
  action,
  tone = 'neutral',
  test_id,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  tone?: 'neutral' | 'destructive';
  test_id?: string;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center"
      data-testid={test_id}
    >
      <div
        className={cn(
          'flex h-11 w-11 items-center justify-center rounded-panel',
          tone === 'destructive'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-heading text-base font-bold">{title}</p>
      {description && <p className="max-w-md text-data text-muted-foreground">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
